import { ComponentType, lazy } from 'react';

const RELOADED_FOR_KEY = 'chunkReloadedFor';

/**
 * Whether a lazy part failed because the file it asked for is gone. Every deploy renames these files, and a tab opened
 * before it still asks for the old name: CloudFront answers with index.html, which fails to run as a script ("Unexpected
 * token '<'", WEB-APP-1J4) and webpack rejects the import with a ChunkLoadError.
 */
export const isChunkLoadError = (error: any) =>
  error?.name === 'ChunkLoadError' || /Loading (CSS )?chunk .* failed/i.test(error?.message || '');

/**
 * `React.lazy`, except that a part left behind by a deploy reloads the page onto the new version. Without it the failed
 * import unmounts the whole app and leaves a blank page until the student reloads themselves, which is what a student
 * opening a task's details hit after the 2026-10-20 deploy (Mason, 2026-10-21).
 *
 * Reloads once per missing file: when the same file fails again after reloading, the reload didn't help, so it is left
 * to fail rather than reload again (a slow device can take long to reload, so no time limit can tell this apart). The
 * next deploy renames the files, so it gets a reload of its own. Never reloads where it can't remember having done so.
 */
export const lazyWithReload = <T extends ComponentType<any>>(importCallback: () => Promise<{ default: T }>) =>
  lazy(() =>
    importCallback().catch(error => {
      if (!isChunkLoadError(error)) throw error;
      // The file's address: webpack and the CSS loader both give it as `request`, and in the message.
      const file = String(error.request || error.message);
      try {
        if (window.sessionStorage.getItem(RELOADED_FOR_KEY) === file) throw error;
        window.sessionStorage.setItem(RELOADED_FOR_KEY, file);
      } catch {
        throw error;
      }
      window.location.reload();
      // The page is going; Suspense keeps showing its fallback until it does.
      return new Promise<{ default: T }>(() => undefined);
    })
  );
